/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Video } from '@atlassian/learning-components';
import { useParams } from 'react-router-dom';
import { throttle } from 'lodash';

import { convertToSeconds } from 'src/pages/LessonPage/children/utils/mediaUtils';
import { sendTrackEvent, sendUIEvent } from 'src/utils/analytics';
import { isStandalone } from 'src/pages/LessonPage/utils/isStandalone';
import { getVideoAttrs } from 'src/utils/contentful';
import { OrVideo } from 'src/graphql/generated/graphql';
import { handlePlyrError } from 'src/pages/LessonPage/utils/plyrErrorHandlingUtils';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';

interface VideoBlockProps {
  entry: any;
  fields: Record<string, any>;
}

export const VideoBlock: React.FC<VideoBlockProps> = ({ entry, fields }) => {
  const videoProgressRef = useRef<{ videoProgress: number; name: string; videoId: string }>();
  const lastActionRef = useRef<string | undefined>(undefined);

  const { courseId: courseSlug, lessonId: lessonSlug } = useParams();

  const { user } = useAtlassianUser();

  const videoAttrs = useMemo(() => getVideoAttrs(fields as OrVideo), [fields]);

  const handleControlClick = useCallback(
    (type: string) => {
      lastActionRef.current = type;

      sendUIEvent(
        lessonSlug ? 'lessonScreen' : 'courseScreen',
        'button',
        'clicked',
        ['play', 'pause'].includes(type) ? 'videoPlayButton' : 'videoCaptionsButton',
        {
          name: entry.name,
          videoId: entry.sys.id,
          courseSlug,
          lessonSlug,
          type,
          ...(lessonSlug && isStandalone() ? { isStandaloneLesson: true } : {}),
        },
        user?.account_id,
      );
    },
    [lessonSlug, courseSlug, user, entry],
  );

  // throttle, because plyr sometimes fires it multiple times
  const handleVideoProgressUpdate = throttle((videoProgress: number, name: string, videoId: string) => {
    videoProgressRef.current = { videoProgress, name, videoId };

    if (videoProgress === 100) {
      sendTrackEvent(
        'lessonScreen',
        'video',
        'completed',
        {
          name,
          courseSlug,
          lessonSlug,
          videoProgress,
          videoId,
        },
        user?.account_id,
      );
    }
  }, 500);

  useEffect(() => {
    const handleBeforeUnload = (): void => {
      const currentProgress = videoProgressRef.current?.videoProgress || NaN;
      const isMediaStartedButNotEnded = currentProgress !== 100 && currentProgress > 1;

      if (isMediaStartedButNotEnded) {
        sendTrackEvent(
          'lessonScreen',
          'video',
          'abandoned',
          {
            courseSlug,
            lessonSlug,
            ...videoProgressRef.current,
          },
          user?.account_id,
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return (): void => {
      handleBeforeUnload();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [courseSlug, lessonSlug, user]);

  const handleError = useCallback((plyrError: Plyr.PlyrEvent) => {
    handlePlyrError('video', plyrError, { lastAction: lastActionRef.current });
  }, []);

  useEffect(() => {
    lastActionRef.current = undefined;
    videoProgressRef.current = undefined;
  }, [lessonSlug, courseSlug]);

  const chapters = fields?.chaptersCollection?.items.map(
    ({ chapterTitle, timeStamp }: { chapterTitle: string; timeStamp: string }) => ({
      time: convertToSeconds(timeStamp),
      label: chapterTitle,
    }),
  );

  return (
    <Video
      aria-label={entry.name}
      name={entry.name}
      videoId={entry.sys.id}
      orangeUrl={videoAttrs?.cdnUrl}
      vimeoUrl={videoAttrs?.vimeoUrl}
      youtubeUrl={videoAttrs?.youtubeUrl}
      captions={videoAttrs?.cdnCaptions || ''}
      chapters={chapters ? chapters : null}
      width={fields?.videoWidth ? fields.videoWidth : 'auto'}
      height={fields?.videoHeight ? fields.videoHeight : 'auto'}
      onControlClick={handleControlClick}
      onProgressUpdate={handleVideoProgressUpdate}
      onError={handleError}
      controls
    />
  );
};
